/* eslint-disable class-methods-use-this */
import {
  ExtendedTextNode,
  SerializedExtendedTextNode,
} from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';
import { MentionNodeToken } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionNodeToken';
import { IconType } from '@mutiny-pkg/dumpster-ui/icons';
import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type NodeKey,
  type Spread,
} from 'lexical';
import * as React from 'react';
import * as ReactDOMServer from 'react-dom/server';

export type SerializedMentionNode = Spread<
  {
    label?: string;
    value: string;
    iconComponent?: IconType;
  },
  SerializedExtendedTextNode
>;

function $convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const { textContent } = domNode;

  if (textContent !== null) {
    const node = $createMentionNode(textContent);
    return {
      node,
    };
  }

  return null;
}

export class MentionNode extends ExtendedTextNode {
  __value: string;

  __label: string;

  __iconComponent?: IconType;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__value, node.__label, node.__key, node.__iconComponent);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.value, serializedNode.label, serializedNode.iconComponent);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setStyle(serializedNode.style);
    node.setHighlightColor(serializedNode.highlightColor);
    node.setFontColor(serializedNode.fontColor);
    return node;
  }

  constructor(value: string, label?: string, key?: NodeKey, iconComponent?: IconType) {
    const textContent = label ?? value ?? '';
    super(textContent, key);
    this.__value = value;
    this.__label = label ?? value;
    this.__iconComponent = iconComponent;
  }

  getValue(): string {
    return this.__value;
  }

  getLabel(): string | undefined {
    return this.__label;
  }

  getIconComponent(): IconType | undefined {
    return this.__iconComponent;
  }

  setLabel(label: string): void {
    this.__text = label;
    this.__label = label;
  }

  setIconComponent(iconComponent: IconType): void {
    this.__iconComponent = iconComponent;
  }

  /**
   * Check if this node's textContent is consistent with the mention value. A node can become inconsistent
   * if the mention value is changed by the editor, such when a segment of the label is deleted.
   */
  isConsistent(): boolean {
    return this.__value === this.__label || this.__text === this.__label;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      value: this.__value,
      label: this.__label,
      iconComponent: this.__iconComponent,
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = `mention ${config.theme.mention} ${dom.className}`;
    dom.spellcheck = false;

    // Clear any existing content
    dom.innerHTML = '';
    dom.innerHTML = this.generateTokenHTML();
    return dom;
  }

  updateDOM(prevNode: ExtendedTextNode, dom: HTMLElement): boolean {
    // Don't call super.updateDOM() to avoid Firefox diffComposedText issue

    // Re-render the rich content if the node has changed
    if (prevNode instanceof MentionNode) {
      const prevMentionNode = prevNode as MentionNode;
      if (prevMentionNode.__label !== this.__label || prevMentionNode.__iconComponent !== this.__iconComponent) {
        // Clear existing content
        dom.innerHTML = '';
        dom.innerHTML = this.generateTokenHTML();
        return true;
      }
    }

    return false;
  }

  /**
   * The only time this should ever be getting called is if we are in the editor
   * & also there is no value for the mention.
   * Eg. `editor.read()` in ../ast.ts and ../ssr.ts
   */
  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.className = 'mention-missing';
    element.spellcheck = false;

    // Clear any existing content
    element.innerHTML = '';
    element.innerHTML = this.generateTokenHTML();

    element.setAttribute('data-lexical-mention', 'true');
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: $convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  private generateTokenHTML(): string {
    return ReactDOMServer.renderToString(
      React.createElement(MentionNodeToken, {
        iconComponent: this.__iconComponent,
        label: this.__label,
      }),
    );
  }
}

export function $createMentionNode(value: string, label?: string, iconComponent?: IconType): MentionNode {
  const mentionNode = new MentionNode(value, label, undefined, iconComponent);
  return $applyNodeReplacement(mentionNode);
}
